import { defineStore } from 'pinia'
import { wxLogin } from '@/api/wechat'
import { carrierLogin } from '@/api/carrierAuth'
import { mobileLogin } from '@/api/mobileAuth'
import { checkToken, updateServiceLocation } from '@/api/user'

export const useUserStore = defineStore('user', {
  state: () => ({
    token: localStorage.getItem('token') || null,
    userInfo: JSON.parse(localStorage.getItem('user') || 'null'),
    lastLoginTime: localStorage.getItem('lastLoginTime') || null,
    isInitComplete: false
  }),
  getters: {
    safeUserInfo: (state) => state.userInfo || {},
    // 获取 userId（兼容不同字段名）
    userId: (state) => state.userInfo?.userId || state.userInfo?.id || null,
    hasAvatar: (state) => !!state.userInfo?.avatar,
    isAuthenticated: (state) => !!state.token && !state.isTokenExpired,
    hasMobile: (state) => !!(state.userInfo?.mobile || state.userInfo?.mobilePartial),
    isTokenExpired: (state) => {
      if (!state.lastLoginTime) return true
      return Date.now() - new Date(state.lastLoginTime).getTime() > 365 * 24 * 60 * 60 * 1000
    }
  },
  actions: {
    // 微信登录
    async wxUserLogin(code) {
      const { token, nickname, avatar, status, userId, id } = await wxLogin(code)

      // 0:禁用, 1:正常, 2:关闭
      if (status !== 1) {
        const statusMsg = status === 0 ? '禁用' : '关闭'
        return {
          success: false,
          status: status,
          message: `帐号已${statusMsg}，请联系平台`
        }
      }

      this.token = token
      this.userInfo = { userId: userId || id, nickname, avatar }
      this.lastLoginTime = new Date().toISOString()

      // 使用localStorage持久化存储
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify({
        userId: userId || id,
        nickname,
        avatar,
      }))
      localStorage.setItem('lastLoginTime', this.lastLoginTime)
      // 单独保存 userId
      if (userId || id) {
        localStorage.setItem('userId', String(userId || id));
      }

      // 更新用户服务地区信息
      this.updateUserLocation()

      return { success: true }
    },

    // 运营商一键登录
    async carrierUserLogin(carrierToken, carrierType) {
      const result = await carrierLogin({
        carrierToken,
        carrierType
      })
      const { token, nickname, avatar, status, userId, id } = result

      // 0:禁用, 1:正常, 2:关闭
      if (status !== 1) {
        const statusMsg = status === 0 ? '禁用' : '关闭'
        return {
          success: false,
          status: status,
          message: `帐号已${statusMsg}，请联系平台`
        }
      }

      this.token = token
      this.userInfo = { userId: userId || id, nickname, avatar }
      this.lastLoginTime = new Date().toISOString()

      // 使用localStorage持久化存储
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify({
        userId: userId || id,
        nickname,
        avatar,
      }))
      localStorage.setItem('lastLoginTime', this.lastLoginTime)
      // 单独保存 userId
      if (userId || id) {
        localStorage.setItem('userId', String(userId || id));
      }

      // 更新用户服务地区信息
      this.updateUserLocation()

      return { success: true }
    },

    // 手机号验证码登录
    async mobileUserLogin(mobile, code) {
      try {
        const result = await mobileLogin({ mobile, code });
        const { token, nickname, avatar, status, userId, id } = result;

        // 0:禁用, 1:正常, 2:关闭
        if (status !== 1) {
          const statusMsg = status === 0 ? '禁用' : '关闭';
          return {
            success: false,
            status: status,
            message: `帐号已${statusMsg}，请联系平台`
          };
        }

        // 保存手机号脱敏信息
        const mobilePartial = mobile.substring(0, 3) + "****" + mobile.substring(mobile.length - 4);

        this.token = token;
        // 保存完整的用户信息（包括 userId）
        this.userInfo = {
          userId: userId || id,
          nickname,
          avatar,
          mobilePartial
        };
        this.lastLoginTime = new Date().toISOString();

        // 使用localStorage持久化存储
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(this.userInfo));
        localStorage.setItem('lastLoginTime', this.lastLoginTime);
        // 单独保存 userId，方便快速访问
        if (this.userInfo.userId) {
          localStorage.setItem('userId', String(this.userInfo.userId));
        }

        // 更新用户服务地区信息
        this.updateUserLocation()

        return { success: true };
      } catch (error) {
        // API 返回错误（如验证码过期、验证码错误等）
        console.error('手机号登录API错误:', error);
        return {
          success: false,
          isApiError: true,
          message: error.message || '登录失败，请重试'
        };
      }
    },

    // 初始化store
    async init() {
      try {
        if (this.token && !this.isTokenExpired) {
          const isValid = await checkToken(this.token)
          console.log("checkToken:", isValid);
          if (isValid != true) this.clearAuth()
        } else {
          this.clearAuth()
        }
      } catch (error) {
        console.error('Token验证失败:', error)
        this.clearAuth()
      } finally {
        this.isInitComplete = true
      }
    },
    // 清除认证信息
    clearAuth() {
      this.$reset()
      this.token = null
      this.userInfo = null
      this.lastLoginTime = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('lastLoginTime')
      localStorage.removeItem('userId')
    },
    // 退出登录
    logout() {
      this.clearAuth()
    },

    // ========== 更新用户服务地区信息 ==========
    async updateUserLocation() {
      try {
        const locationInfo = localStorage.getItem('user_location_info')
        if (locationInfo) {
          const parsed = JSON.parse(locationInfo)
          if (parsed.province || parsed.city || parsed.name) {
            await updateServiceLocation({
              province: parsed.province || '',
              city: parsed.city || '',
              name: parsed.name || '',
              lng: parsed.longitude || 0,
              lat: parsed.latitude || 0
            })
            console.log('用户服务地区信息更新成功')
          }
        }
      } catch (error) {
        console.error('更新用户服务地区信息失败:', error)
        // 不阻塞登录流程，静默失败
      }
    },

    // ========== 新增：更新用户信息的 action ==========
    updateUserInfo(newInfo) {
      // 合并新信息到原始 userInfo（保持响应式）
      this.userInfo = {
        ...this.userInfo,
        ...newInfo
      };
      // 同步更新本地存储（与仓库状态一致）
      localStorage.setItem('user', JSON.stringify(this.userInfo));
      // 单独保存 userId，方便快速访问
      if (this.userInfo.userId) {
        localStorage.setItem('userId', String(this.userInfo.userId));
      }
    }
  }
})
